// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

// webgen:import { "names": ["CalculateOtherCost", "CalculateSuggestedPrice", "CalculateTargetMarginFlatAmount", "CalculateMinimumMarginFlatAmount"], "path": "../../../utils/costCalculations" }
// webgen:import { "wildcardName": "CurrencyHelpers", "path": "../../../utils/currency" }
// webgen:import { "names": ["parseDecimalToNumber"], "path": "../../../utils/decimals" }
// webgen:import { "names": ["TenantDefaultMargins"], "path": "../../tenantDefaultMargins" }
// webgen:import { "names": ["hasValue", "hasStringValue"], "path": "../../../utils/equality" }
// webgen:import { "names": ["getCurrencySymbol"], "path": "../../../utils/format" }

import type { PartialDeep } from 'type-fest';

import {
  CalculateMinimumMarginFlatAmount,
  CalculateOtherCost,
  CalculateSuggestedPrice,
  CalculateTargetMarginFlatAmount,
} from '../../../utils/costCalculations';
import * as CurrencyHelpers from '../../../utils/currency';
import { parseDecimalToNumber } from '../../../utils/decimals';
import { hasStringValue, hasValue } from '../../../utils/equality';
import { getCurrencySymbol } from '../../../utils/format';
import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import { TenantDefaultMargins } from '../../tenantDefaultMargins';
import { Currency, type CurrencyWireFormat, NewCurrency } from '../Currency/Currency';
import Enums from '../Enums';
import {
  type IntegrationSummary,
  type IntegrationSummaryWireFormat,
  NewIntegrationSummary,
} from '../IntegrationSummary/IntegrationSummary';
import { type Material, type MaterialWireFormat, NewMaterial } from '../Material/Material';
import { type Mix, type MixWireFormat, NewMix } from '../Mix/Mix';
import { NewPlant, Plant, type PlantWireFormat } from '../Plant/Plant';
import { NewTier, type Tier, type TierWireFormat } from '../Tier/Tier';

export class Product {
  readonly id: string;
  readonly alternateID: string;
  readonly name: string;
  readonly category: Enums.ProductCategory;
  readonly quantity: number;
  readonly quantityUnit: Enums.Unit;
  readonly listPrice: Currency | null;
  readonly targetMarginPercentageOverride: string | null;
  readonly minimumMarginPercentageOverride: string | null;
  readonly targetMarginFlatAmountOverride: Currency | null;
  readonly minimumMarginFlatAmountOverride: Currency | null;
  readonly targetMargin: string;
  readonly minimumMargin: string;
  readonly marginsInheritedFrom: string;
  readonly materialCostOverride: Currency | null;
  readonly otherCostOverride: Currency | null;
  readonly mix: Mix | null;
  readonly resaleMaterial: Material | null;
  readonly aggregateMaterial: Material | null;
  readonly asphaltMix: Mix | null;
  readonly plant: Plant;
  readonly divisionId: string | null;
  readonly externalID: string | null;
  readonly isArchived: boolean;
  readonly integrationSummary: IntegrationSummary | null;
  readonly materialCost: Currency;
  readonly otherCost: Currency;
  readonly incompatibleMixQuantityUnits: boolean;
  readonly tiers: Tier[];

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.alternateID = data.alternateID === undefined ? '' : data.alternateID;
    this.name = data.name === undefined ? '' : data.name;
    this.category = data.category === undefined ? Enums.ProductCategory.Other : data.category;
    this.quantity =
      (data.cuydVolume ?? data.quantity) === undefined ? 0 : (data.cuydVolume ?? data.quantity);
    this.quantityUnit =
      (data.measurementUnit ?? data.quantityUnit) === undefined
        ? Enums.Unit.Ac
        : (data.measurementUnit ?? data.quantityUnit);
    this.listPrice = (data.listPrice ?? null) === null ? null : NewCurrency(data.listPrice);
    this.targetMarginPercentageOverride =
      data.targetMarginPercentageOverride === undefined
        ? null
        : data.targetMarginPercentageOverride;
    this.minimumMarginPercentageOverride =
      data.minimumMarginPercentageOverride === undefined
        ? null
        : data.minimumMarginPercentageOverride;
    this.targetMarginFlatAmountOverride =
      (data.targetMarginFlatAmountOverride ?? null) === null
        ? null
        : NewCurrency(data.targetMarginFlatAmountOverride);
    this.minimumMarginFlatAmountOverride =
      (data.minimumMarginFlatAmountOverride ?? null) === null
        ? null
        : NewCurrency(data.minimumMarginFlatAmountOverride);
    this.targetMargin = data.targetMargin === undefined ? '' : data.targetMargin;
    this.minimumMargin = data.minimumMargin === undefined ? '' : data.minimumMargin;
    this.marginsInheritedFrom =
      data.marginsInheritedFrom === undefined ? '' : data.marginsInheritedFrom;
    this.materialCostOverride =
      (data.materialCostOverride ?? null) === null ? null : NewCurrency(data.materialCostOverride);
    this.otherCostOverride =
      (data.otherCostOverride ?? null) === null ? null : NewCurrency(data.otherCostOverride);
    this.mix =
      // eslint-disable-next-line no-nested-ternary
      (data.mix ?? null) === null ? null : NewMix(data.mix);
    this.resaleMaterial =
      // eslint-disable-next-line no-nested-ternary
      (data.resaleMaterial ?? null) === null ? null : NewMaterial(data.resaleMaterial);
    this.aggregateMaterial =
      // eslint-disable-next-line no-nested-ternary
      (data.aggregateMaterial ?? null) === null ? null : NewMaterial(data.aggregateMaterial);
    this.asphaltMix =
      // eslint-disable-next-line no-nested-ternary
      (data.asphaltMix ?? null) === null ? null : NewMix(data.asphaltMix);
    this.plant = NewPlant(data.plant);
    this.divisionId = data.divisionId === undefined ? null : data.divisionId;
    this.externalID =
      // eslint-disable-next-line no-nested-ternary
      (data.externalID ?? null) === null
        ? null
        : data.externalID === undefined
          ? ''
          : data.externalID;
    this.isArchived = data.isArchived === undefined ? false : data.isArchived;
    this.integrationSummary =
      // eslint-disable-next-line no-nested-ternary
      (data.integration ?? data.integrationSummary ?? null) === null
        ? null
        : NewIntegrationSummary(data.integration ?? data.integrationSummary);
    this.materialCost = NewCurrency(data.materialCost);
    this.otherCost = NewCurrency(data.otherCost);
    this.incompatibleMixQuantityUnits =
      (data.incompatibleMixBatchUnits ?? data.incompatibleMixQuantityUnits) === undefined
        ? false
        : (data.incompatibleMixBatchUnits ?? data.incompatibleMixQuantityUnits);
    this.tiers = (data.tiers ?? []).map((tiers: any) => NewTier(tiers));
  }

  static zero(): Product {
    const zeroValues: ExcludeMethodsDeep<Product> = {
      id: NIL_UUID,
      alternateID: '',
      name: '',
      category: Enums.ProductCategory.Other,
      quantity: 0,
      quantityUnit: Enums.Unit.Ac,
      listPrice: null,
      targetMarginPercentageOverride: null,
      minimumMarginPercentageOverride: null,
      targetMarginFlatAmountOverride: null,
      minimumMarginFlatAmountOverride: null,
      targetMargin: '',
      minimumMargin: '',
      marginsInheritedFrom: '',
      materialCostOverride: null,
      otherCostOverride: null,
      mix: null,
      resaleMaterial: null,
      aggregateMaterial: null,
      asphaltMix: null,
      plant: Plant.zero(),
      divisionId: null,
      externalID: null,
      isArchived: false,
      integrationSummary: null,
      materialCost: Currency.zero(),
      otherCost: Currency.zero(),
      incompatibleMixQuantityUnits: false,
      tiers: [],
    };
    return new Product(zeroValues);
  }

  toJSON(): { [key: string]: any } {
    return {
      id: this.id,
      alternateID: this.alternateID,
      name: this.name,
      category: this.category,
      cuydVolume: this.quantity,
      measurementUnit: this.quantityUnit,
      listPrice: this.listPrice,
      targetMarginPercentageOverride: this.targetMarginPercentageOverride,
      minimumMarginPercentageOverride: this.minimumMarginPercentageOverride,
      targetMarginFlatAmountOverride: this.targetMarginFlatAmountOverride,
      minimumMarginFlatAmountOverride: this.minimumMarginFlatAmountOverride,
      targetMargin: this.targetMargin,
      minimumMargin: this.minimumMargin,
      marginsInheritedFrom: this.marginsInheritedFrom,
      materialCostOverride: this.materialCostOverride,
      otherCostOverride: this.otherCostOverride,
      mix: this.mix,
      resaleMaterial: this.resaleMaterial,
      aggregateMaterial: this.aggregateMaterial,
      asphaltMix: this.asphaltMix,
      plant: this.plant,
      divisionId: this.divisionId,
      externalID: this.externalID,
      isArchived: this.isArchived,
      integration: this.integrationSummary,
      materialCost: this.materialCost,
      otherCost: this.otherCost,
      incompatibleMixBatchUnits: this.incompatibleMixQuantityUnits,
      tiers: this.tiers,
    };
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  /** Returns false if product list price is invalid. */
  hasValidListPrice(): this is Product & { listPrice: Currency } {
    return (
      hasValue(this.listPrice) &&
      this.listPrice.number !== '' &&
      parseFloat(this.listPrice.number) > 0
    );
  }

  /** Returns materialCostOverride if set, otherwise materialCost. */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getMaterialCost() {
    return this.materialCostOverride ?? this.materialCost;
  }

  /** Returns the otherCostOverride if set, otherwise the server-calculated otherCost. */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getOtherCost() {
    return this.otherCostOverride ?? this.otherCost;
  }

  /** Returns the material costs + other costs. This uses the server-calculated other costs. */
  costPerUnit(): Currency {
    return this.getMaterialCost().add(this.getOtherCost()) ?? Currency.zero();
  }

  /**
   * Returns the suggested material cost as a Currency | null.
   *
   * This checks the selected mix or material to dynamically
   * populate a suggested price during creation/editing of a Product.
   */
  suggestedMaterialCostCurrency(): Currency | null {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const materialCostCurrency = (() => {
      if (this.category === Enums.ProductCategory.ReadyMix) {
        return this.mix?.materialCost ?? null;
      }
      if (this.category === Enums.ProductCategory.Asphalt) {
        return this.asphaltMix?.materialCost ?? null;
      }
      // This product quantity unit must match the resale material's inProductCost unit.
      if (this.quantityUnit === this.resaleMaterial?.inProductCost?.unit) {
        return this.resaleMaterial?.inProductCost?.amount ?? null;
      }
      // This product quantity unit must match the aggregate material's inProductCost unit.
      if (this.quantityUnit === this.aggregateMaterial?.inProductCost?.unit) {
        return this.aggregateMaterial?.inProductCost?.amount ?? null;
      }

      // The product is not a mix, asphalt, resale, or aggregate material.
      // Or if it is a resale/aggregate material, the units do not match.
      return null;
    })();

    return materialCostCurrency;
  }

  /**
   * Returns the suggested material cost as a string | null.
   *
   * This checks the selected mix or material to dynamically
   * populate a suggested price during creation/editing of a Product.
   */
  suggestedMaterialCostDisplay(): string | null {
    const materialCost = this.suggestedMaterialCostCurrency();
    if (!hasValue(materialCost)) return null;
    return CurrencyHelpers.CurrencyString({
      cur: materialCost,
      currencyCode: this.plant.currencyCode,
    });
  }

  /** Returns the product's non-material cost (this is otherCostOverride if set, otherwise the calculated cost). */
  calculateOtherCost(): Currency | null {
    return CalculateOtherCost({
      product: this,
      deliveryCosts: this.plant.deliveryCosts,
      otherCostOverride: this.otherCostOverride,
    });
  }

  /** Returns the product's calculated non-material cost, without respecting otherCostOverride. */
  calculateSuggestedOtherCost(): Currency | null {
    return CalculateOtherCost({
      product: this,
      deliveryCosts: this.plant.deliveryCosts,
      otherCostOverride: null,
    });
  }

  /** Returns a suggested price for the material, combining material and other costs with the target margin. */
  calculateSuggestedPrice(
    marginConfiguration: Enums.MarginConfiguration,
    defaultMargins: TenantDefaultMargins,
  ): Currency | null {
    return CalculateSuggestedPrice({
      product: this,
      deliveryCosts: this.plant.deliveryCosts,
      otherCostOverride: this.otherCostOverride,
      marginConfiguration,
      defaultMargins,
    });
  }

  targetMarginPercentage(defaultMargins: TenantDefaultMargins): number | null {
    // Product-level override (stored as decimal string)
    if (hasStringValue(this.targetMarginPercentageOverride ?? '')) {
      return 100 * parseDecimalToNumber(this.targetMarginPercentageOverride, 0);
    }
    // Location-level override or fallback to tenant defaults when location constants are missing
    if (this.plant.readyMixConstants !== null) {
      return this.plant.readyMixConstants.targetMarginPercentage(
        defaultMargins.targetMarginPercentage,
      );
    }
    // Tenant default (decimal string) → percentage
    const tenantDefault = Number(defaultMargins.targetMarginPercentage);
    if (!Number.isFinite(tenantDefault)) {
      return 0;
    }
    return tenantDefault * 100;
  }

  targetMarginPercentageDisplay(defaultMargins: TenantDefaultMargins): string | null {
    const value = this.targetMarginPercentage(defaultMargins);
    const safe = value === null || Number.isNaN(value) ? 0 : value;
    return `${safe.toLocaleString('en-US')}%`;
  }

  targetMarginFlatAmount(defaultMargins: TenantDefaultMargins): Currency {
    return CalculateTargetMarginFlatAmount({
      product: this,
      defaultMargins,
    });
  }

  targetMarginFlatAmountDisplay(defaultMargins: TenantDefaultMargins): string | null {
    return CurrencyHelpers.CurrencyString({
      cur: this.targetMarginFlatAmount(defaultMargins),
      currencyCode: this.plant.currencyCode,
    });
  }

  targetMarginDisplay(
    marginConfiguration: Enums.MarginConfiguration,
    defaultMargins: TenantDefaultMargins,
  ): string | null {
    return marginConfiguration === Enums.MarginConfiguration.Percentage
      ? this.targetMarginPercentageDisplay(defaultMargins)
      : this.targetMarginFlatAmountDisplay(defaultMargins);
  }

  minimumMarginPercentage(defaultMargins: TenantDefaultMargins): number | null {
    // Product-level override (stored as decimal string)
    if (hasStringValue(this.minimumMarginPercentageOverride ?? '')) {
      return parseFloat((parseFloat(this.minimumMarginPercentageOverride ?? '0') * 100).toFixed(3));
    }
    // Location-level override or fallback to tenant defaults when location constants are missing
    if (this.plant.readyMixConstants !== null) {
      return this.plant.readyMixConstants.minimumMarginPercentage(
        defaultMargins.minimumMarginPercentage,
      );
    }
    // Tenant default (decimal string) → percentage
    const tenantDefault = Number(defaultMargins.minimumMarginPercentage);
    if (!Number.isFinite(tenantDefault)) {
      return 0;
    }
    return parseFloat((tenantDefault * 100).toFixed(3));
  }

  minimumMarginPercentageDisplay(defaultMargins: TenantDefaultMargins): string | null {
    const value = this.minimumMarginPercentage(defaultMargins);
    const safe = value === null || Number.isNaN(value) ? 0 : value;
    return `${safe.toLocaleString('en-US')}%`;
  }

  minimumMarginFlatAmount(defaultMargins: TenantDefaultMargins): Currency {
    return CalculateMinimumMarginFlatAmount({
      product: this,
      defaultMargins,
    });
  }

  minimumMarginFlatAmountDisplay(defaultMargins: TenantDefaultMargins): string {
    return CurrencyHelpers.CurrencyString({
      cur: this.minimumMarginFlatAmount(defaultMargins),
      currencyCode: this.plant.currencyCode,
    });
  }

  minimumMarginDisplay(
    marginConfiguration: Enums.MarginConfiguration,
    defaultMargins: TenantDefaultMargins,
  ): string | null {
    return marginConfiguration === Enums.MarginConfiguration.Percentage
      ? this.minimumMarginPercentageDisplay(defaultMargins)
      : this.minimumMarginFlatAmountDisplay(defaultMargins);
  }

  totalMarginPercentage(): number | null {
    if (!this.hasValidListPrice()) return null;

    const totalCost = this.costPerUnit().toUnit();
    const listPrice = this.listPrice.toUnit();
    const marginNumber = (1 - totalCost / listPrice) * 100;

    // TODO: Replace this with decimal precision in sc-1251
    return parseFloat(marginNumber.toFixed(3));
  }

  totalMarginPercentageDisplay(): string | null {
    const thisTotalMarginPercentage = this.totalMarginPercentage();
    return thisTotalMarginPercentage === null
      ? null
      : `${thisTotalMarginPercentage.toLocaleString('en-US')}%`;
  }

  totalMarginFlatAmount(): Currency {
    if (!this.hasValidListPrice()) return Currency.zero();

    const totalCost = this.costPerUnit();
    const listPrice = this.listPrice;

    // Handle edge cases
    if (totalCost.toUnit() < 0 || listPrice.toUnit() <= 0) {
      return Currency.zero();
    }

    return listPrice.subtract(totalCost) ?? Currency.zero();
  }

  totalMarginFlatAmountDisplay(): string | null {
    return CurrencyHelpers.CurrencyString({
      cur: this.totalMarginFlatAmount(),
      currencyCode: this.plant.currencyCode,
    });
  }

  /** Returns the margin as a percentage string | null */
  totalMarginDisplay(marginConfiguration: Enums.MarginConfiguration): string | null {
    return marginConfiguration === Enums.MarginConfiguration.Percentage
      ? this.totalMarginPercentageDisplay()
      : this.totalMarginFlatAmountDisplay();
  }

  marginOverMaterialPercentage(): number | null {
    if (!this.hasValidListPrice()) return null;
    const materialCost = this.getMaterialCost().toUnit();
    const listPrice = this.listPrice.toUnit();
    return (1 - materialCost / listPrice) * 100;
  }

  marginOverMaterialPercentageDisplay(): string | null {
    const value = this.marginOverMaterialPercentage();
    return value === null ? value : `${parseFloat(value.toFixed(3)).toLocaleString('en-US')}%`;
  }

  marginOverMaterialFlatAmount(): Currency | null {
    if (!this.hasValidListPrice()) return null;
    return this.listPrice.subtract(this.getMaterialCost());
  }

  marginOverMaterialFlatAmountDisplay(): string | null {
    const value = this.marginOverMaterialFlatAmount();
    return value === null
      ? value
      : CurrencyHelpers.CurrencyString({ cur: value, currencyCode: this.plant.currencyCode });
  }

  /** Returns the margin over material as a percentage string | null */
  marginOverMaterialDisplay(marginConfiguration: Enums.MarginConfiguration): string | null {
    return marginConfiguration === Enums.MarginConfiguration.Percentage
      ? this.marginOverMaterialPercentageDisplay()
      : this.marginOverMaterialFlatAmountDisplay();
  }

  /** Returns the margin over material as a currency amount string | null */
  marginOverMaterialDisplayAsDollar(): string | null {
    const symbol = getCurrencySymbol(this.plant.currencyCode);
    if (
      this.listPrice === undefined ||
      this.listPrice === null ||
      this.listPrice.number === '' ||
      parseFloat(this.listPrice.number) <= 0
    ) {
      return `${symbol}0.00`;
    }

    const materialCost = this.getMaterialCost().toUnit();
    const listPrice = this.listPrice.toUnit();

    // Handle edge cases
    if (materialCost < 0 || listPrice <= 0) {
      return `${symbol}0.00`;
    }

    const marginOverMaterialDollar = listPrice - materialCost;

    // Handle negative margins (when material cost exceeds price)
    if (marginOverMaterialDollar < 0) {
      return `-${symbol}${Math.abs(marginOverMaterialDollar).toFixed(2)}`;
    }

    return `${symbol}${marginOverMaterialDollar.toFixed(2)}`;
  }

  /** externalName returns the product name in a user-friendly displayable format */
  externalName(): string {
    // Strips the internal " (Table <id>)" suffix set by formatChargeName (internal/series/charges.go).
    const stripped = this.name.replace(/\s*\(Table [^)]+\)$/, '');

    if (this.alternateID === '') {
      return stripped;
    }

    // Escape special regex characters in alternateID
    const escapedAlternateID = this.alternateID.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // [(] and [)]: Match the literal parentheses.
    // .*?: Match any character (except for line terminators)
    //      between zero and unlimited times, as few times as possible.
    // ${escapedAlternateID}: Match the escaped alternateID.
    // no flags: only first instance and case-sensitive match.
    const pattern = new RegExp(` [(].*?${escapedAlternateID}.*?[)]`, '');

    return stripped.replace(pattern, '');
  }

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type ProductWireFormat = {
  id: string;
  alternateID: string;
  name: string;
  category: string;
  quantity: number;
  quantityUnit: string;
  listPrice: CurrencyWireFormat | null;
  targetMarginPercentageOverride: string | null;
  minimumMarginPercentageOverride: string | null;
  targetMarginFlatAmountOverride: CurrencyWireFormat | null;
  minimumMarginFlatAmountOverride: CurrencyWireFormat | null;
  targetMargin: string;
  minimumMargin: string;
  marginsInheritedFrom: string;
  materialCostOverride: CurrencyWireFormat | null;
  otherCostOverride: CurrencyWireFormat | null;
  mix: MixWireFormat | null;
  resaleMaterial: MaterialWireFormat | null;
  aggregateMaterial: MaterialWireFormat | null;
  asphaltMix: MixWireFormat | null;
  plant: PlantWireFormat;
  divisionId: string | null;
  externalID: string | null;
  isArchived: boolean;
  integrationSummary: IntegrationSummaryWireFormat | null;
  materialCost: CurrencyWireFormat;
  otherCost: CurrencyWireFormat;
  incompatibleMixQuantityUnits: boolean;
  tiers: TierWireFormat[];
};

type NewProductProps = PartialDeep<Product, { recurseIntoArrays: true }>;
export const NewProduct: (props: NewProductProps) => Product = (props) => new Product(props);

type NewProductFromWireFormatProps = PartialDeep<ProductWireFormat, { recurseIntoArrays: true }>;
export const NewProductFromWireFormat: (props: NewProductFromWireFormatProps) => Product = (
  props,
) => new Product(props);
